import { useState, type FormEvent } from 'react';
import { useAuth } from '@/components/feature/AuthContext';
import { submitFeedbackInfo } from '@/services/content';

export type QuickFormType = 'alliance' | 'service' | 'cooperation' | 'realworld';

interface QuickFormMeta {
  eyebrow: string;
  title: string;
  description: string;
  icon: string;
}

interface QuickFormConfig {
  /** 提交标题前缀（后台意见反馈列表中的标题） */
  titlePrefix: string;
  formId: string;
  submitLabel: string;
  successTitle: string;
  successText: string;
  selectLabel: string;
  selectOptions: string[];
  textareaLabel: string;
  textareaPlaceholder: string;
}

export const QUICK_FORM_META: Record<QuickFormType, QuickFormMeta> = {
  alliance: {
    eyebrow: '专科联盟',
    title: '专科联盟 · 发起 / 加入申请',
    description: '填写联盟发起或加入意向，联盟秘书处将在 1-2 个工作日内与您联系。',
    icon: 'ri-links-line',
  },
  service: {
    eyebrow: '数据服务',
    title: '数据服务需求登记',
    description: '描述您的数据治理、开发与入表需求，专业顾问将尽快与您对接。',
    icon: 'ri-database-2-line',
  },
  cooperation: {
    eyebrow: '生态合作',
    title: '生态合作需求登记',
    description: '留下您的合作意向，我们将安排专人与您对接合作事宜。',
    icon: 'ri-team-line',
  },
  realworld: {
    eyebrow: '真实世界',
    title: '真实世界研究需求登记',
    description: '登记真实世界研究的发起、加入、委托、合作与科研支持需求，专业团队将尽快与您对接。',
    icon: 'ri-line-chart-line',
  },
};

const QUICK_FORM_CONFIG: Record<QuickFormType, QuickFormConfig> = {
  alliance: {
    titlePrefix: '专科联盟申请',
    formId: 'quick-alliance-form',
    submitLabel: '提交申请',
    successTitle: '申请已提交',
    successText: '感谢您的申请，联盟秘书处将在 1-2 个工作日内与您电话确认，请保持手机畅通。',
    selectLabel: '申请类型',
    selectOptions: ['发起专科联盟', '加入专科联盟'],
    textareaLabel: '联盟方向与意向说明',
    textareaPlaceholder: '请简要说明拟共建的专科方向、现有资源与期望的合作方式',
  },
  service: {
    titlePrefix: '数据服务需求登记',
    formId: 'quick-service-form',
    submitLabel: '提交需求',
    successTitle: '需求已提交',
    successText: '感谢您的留言，数据服务顾问将在 1-2 个工作日内与您联系，进一步了解您的具体需求。',
    selectLabel: '服务类型',
    selectOptions: ['数据治理', '数据开发', '数据资产入表', '科研数据服务', '其他'],
    textareaLabel: '需求描述',
    textareaPlaceholder: '请描述您的数据规模、应用场景与期望的交付成果',
  },
  cooperation: {
    // 提交到自有平台（/content/submitFeedbackInfo）
    titlePrefix: '生态合作需求登记',
    formId: 'quick-cooperation-form',
    submitLabel: '提交需求',
    successTitle: '需求已提交',
    successText: '感谢您的信任，合作对接专员将在 1-2 个工作日内与您联系，共同探讨合作可能。',
    selectLabel: '合作类型',
    selectOptions: ['机构入驻', '技术合作', '产业合作', '学术共建', '其他'],
    textareaLabel: '合作需求描述',
    textareaPlaceholder: '请简要说明您的机构情况、资源禀赋与合作期望',
  },
  realworld: {
    titlePrefix: '真实世界研究需求登记',
    formId: 'quick-realworld-form',
    submitLabel: '提交需求',
    successTitle: '需求已提交',
    successText: '感谢您的提交，真实世界研究团队将在 1-2 个工作日内与您联系，进一步对接研究需求与支持内容。',
    selectLabel: '需求类型',
    selectOptions: ['研究发起', '研究加入', '研究委托', '生态合作', '科研需求及支持'],
    textareaLabel: '需求描述',
    textareaPlaceholder: '请简要说明研究方向、病种领域、数据条件与期望的支持内容',
  },
};

interface QuickEntryFormProps {
  type: QuickFormType;
}

export default function QuickEntryForm({ type }: QuickEntryFormProps) {
  const config = QUICK_FORM_CONFIG[type];
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const { requireLogin } = useAuth();

  /** 提交到自有平台（意见反馈接口），提交前需登录 */
  const submitToPlatform = async (data: FormData, formEl: HTMLFormElement) => {
    const org = String(data.get('单位名称') ?? '').trim();
    const contact = String(data.get('联系人') ?? '').trim();
    const phone = String(data.get('手机号') ?? '').trim();
    const email = String(data.get('email') ?? '').trim();
    const selectValue = String(data.get(config.selectLabel) ?? '').trim();
    const desc = String(data.get(config.textareaLabel) ?? '').trim();
    if (!org || !contact || !phone || !selectValue || !desc) {
      setStatus('error');
      setFormError(
        `请完整填写单位名称、联系人、手机号、${config.selectLabel}与${config.textareaLabel}。`,
      );
      return;
    }

    // 后端留言字段限 1000 字符，拼接后超长时截断
    let feedback = `单位名称：${org}\n联系人：${contact}\n手机号：${phone}\n`;
    if (email) feedback += `电子邮箱：${email}\n`;
    feedback += `${config.selectLabel}：${selectValue}\n${config.textareaLabel}：${desc}`;
    if (feedback.length > 1000) feedback = feedback.slice(0, 1000);

    setStatus('submitting');
    setFormError('');

    try {
      await submitFeedbackInfo({ name: `${config.titlePrefix} · ${org}`, feedback });
      setStatus('success');
      formEl.reset();
    } catch (err) {
      setStatus('error');
      setFormError(err instanceof Error ? err.message : '提交失败，请稍后重试。');
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;

    if (status === 'submitting') return;

    // 立即在异步操作前读取真实表单值（含浏览器自动填充）
    const data = new FormData(formEl);

    // 反垃圾蜜罐：被填写则静默视为成功，不发送任何请求
    const honey = String(data.get('company_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      formEl.reset();
      return;
    }

    // 未登录先弹登录，登录成功后自动继续提交
    requireLogin(() => {
      void submitToPlatform(data, formEl);
    });
  };

  const inputClass =
    'w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center text-center py-8 px-4 rounded-lg border border-accent-200 bg-accent-50">
        <span className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
          <i className="ri-checkbox-circle-line text-3xl"></i>
        </span>
        <h3 className="font-heading text-lg font-bold text-foreground-900">{config.successTitle}</h3>
        <p className="mt-2 text-sm text-foreground-600 max-w-md">{config.successText}</p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          再填一份
        </button>
      </div>
    );
  }

  return (
    <form id={config.formId} data-readdy-form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor={`${config.formId}-org`} className={labelClass}>
          单位名称 <span className="text-primary-500">*</span>
        </label>
        <input
          id={`${config.formId}-org`}
          name="单位名称"
          required
          maxLength={60}
          placeholder="请输入医院 / 机构 / 企业全称"
          className={inputClass}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${config.formId}-contact`} className={labelClass}>
            联系人 <span className="text-primary-500">*</span>
          </label>
          <input
            id={`${config.formId}-contact`}
            name="联系人"
            required
            maxLength={30}
            placeholder="请输入联系人姓名"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={`${config.formId}-phone`} className={labelClass}>
            手机号 <span className="text-primary-500">*</span>
          </label>
          <input
            id={`${config.formId}-phone`}
            name="手机号"
            type="tel"
            required
            maxLength={20}
            pattern="[0-9+\- ]{6,20}"
            placeholder="用于接收确认通知"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor={`${config.formId}-email`} className={labelClass}>
          电子邮箱
        </label>
        <input
          id={`${config.formId}-email`}
          name="email"
          type="email"
          maxLength={60}
          placeholder="选填，便于接收资料"
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor={`${config.formId}-select`} className={labelClass}>
          {config.selectLabel} <span className="text-primary-500">*</span>
        </label>
        <select id={`${config.formId}-select`} name={config.selectLabel} required defaultValue="" className={inputClass}>
          <option value="" disabled>
            请选择{config.selectLabel}
          </option>
          {config.selectOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={`${config.formId}-desc`} className={labelClass}>
          {config.textareaLabel} <span className="text-primary-500">*</span>
        </label>
        <textarea
          id={`${config.formId}-desc`}
          name={config.textareaLabel}
          required
          maxLength={500}
          rows={3}
          placeholder={config.textareaPlaceholder}
          className={`${inputClass} resize-none`}
        ></textarea>
      </div>

      {/* 反垃圾蜜罐字段 */}
      <input
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-honeypot-aux"
      />

      {status === 'error' && formError && (
        <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3.5 py-2.5">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>{formError}</span>
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i>
            {config.submitLabel}
          </>
        )}
      </button>

      <p className="text-xs text-foreground-500 leading-relaxed text-center">
        提交即表示同意我们使用以上信息与您取得联系，我们将严格保护您的个人隐私。
      </p>
    </form>
  );
}